import { useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import { Box } from '@/shared/ui';

import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';

import { MonthlyRecapsArticleTeaser } from './MonthlyRecapsArticleTeaser';

const ROW_GAP = 16;
const ESTIMATED_ROW_HEIGHT = 140;
const OVERSCAN = 5;
const LAST_ROW_MARGIN_BOTTOM = 16;

type MonthlyRecapsListViewProps = {
  articles: MonthlyRecapsArticle[];
  onSelectArticle: (article: MonthlyRecapsArticle) => void;
};

export const MonthlyRecapsListView = ({
  articles,
  onSelectArticle
}: MonthlyRecapsListViewProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: articles.length,
    gap: ROW_GAP,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: index => articles[index]?.file ?? index
  });

  return (
    <Box
      ref={scrollRef}
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2 }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: articles.length ? rowVirtualizer.getTotalSize() + LAST_ROW_MARGIN_BOTTOM : 0
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualRow => {
          const article = articles[virtualRow.index];
          return (
            <Box
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`
              }}
            >
              <MonthlyRecapsArticleTeaser
                article={article}
                onClick={() => {
                  onSelectArticle(article);
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
